import { useId, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Modal, Textarea } from "../../components/ui";
import type { EditWorkflowNodeRequest } from "./models";
import styles from "./WorkflowBuilder.module.css";

type Props = {
    title: string;
    description: string;
    boxId: string | null;
    revision: number;
    disabled: boolean;
    onSave: (request: EditWorkflowNodeRequest) => Promise<void>;
    onCancel: () => void;
    children?: ReactNode;
};

export function WorkflowNodeEditor({ title, description, boxId, revision, disabled, onSave, onCancel, children }: Props) {
    const { t } = useTranslation();
    const id = useId();
    // Keep the opened version and user input on errors; never overwrite a newer draft.
    const [original] = useState({ title, description, revision });
    const [editedTitle, setEditedTitle] = useState(title);
    const [editedDescription, setEditedDescription] = useState(description);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const pending = useRef<{ fingerprint: string; request: EditWorkflowNodeRequest }>();
    const submitting = useRef(false);
    const stale = revision !== original.revision;
    const descriptionLimit = boxId === null ? 6000 : 12000;
    const valid = Boolean(editedTitle.trim()) && editedTitle.length <= 120 && editedDescription.length <= descriptionLimit
        && (boxId === null || Boolean(editedDescription.trim()));
    const changed = editedTitle !== original.title || editedDescription !== original.description;

    const save = async () => {
        if (disabled || submitting.current || stale || !valid || !changed) return;
        const content = { title: editedTitle, description: editedDescription, expected_revision: original.revision };
        const fingerprint = JSON.stringify(content);
        const request: EditWorkflowNodeRequest = pending.current?.fingerprint === fingerprint ? pending.current.request : {
            ...content,
            request_id: crypto.randomUUID(),
            ...(boxId === null ? { node_kind: "trigger", box_id: null } as const : { node_kind: "step", box_id: boxId } as const)
        };
        pending.current = { fingerprint, request };
        submitting.current = true;
        setSaving(true); setError("");
        try {
            await onSave(request);
        } catch (caught: unknown) {
            setError(caught instanceof Error ? caught.message : t("workflowBuilder.nodeSaveError"));
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    return <form className={styles.nodeEditor} onSubmit={event => { event.preventDefault(); void save(); }} aria-busy={saving}>
        <Modal.Body className={styles.nodeEditorBody}>
            <label className={styles.nodeField} htmlFor={`${id}-title`}>
                <span>{t("workflowBuilder.nodeTitle")}</span>
                <Input id={`${id}-title`} value={editedTitle} maxLength={120} required disabled={disabled || saving || stale}
                    onChange={event => setEditedTitle(event.target.value)} />
            </label>
            <label className={styles.nodeField} htmlFor={`${id}-description`}>
                <span>{t("workflowBuilder.nodeDescription")}</span>
                <Textarea id={`${id}-description`} value={editedDescription} maxLength={descriptionLimit} rows={7}
                    required={boxId !== null} disabled={disabled || saving || stale}
                    onChange={event => setEditedDescription(event.target.value)} />
            </label>
            {children && <div className={styles.nodeMetadata}>{children}</div>}
            {stale && <p role="alert" className={styles.error}>{t("workflowBuilder.nodeChanged")}</p>}
            {error && !stale && <p role="alert" className={styles.error}>{error}</p>}
        </Modal.Body>
        <Modal.Footer className={styles.nodeEditorFooter}>
            <Button type="button" disabled={saving} onClick={onCancel}>{t("workflowBuilder.cancelNodeEdit")}</Button>
            <Button type="submit" variant="primary" isLoading={saving} disabled={disabled || saving || stale || !valid || !changed}>
                {t("workflowBuilder.saveNodeChanges")}
            </Button>
        </Modal.Footer>
    </form>;
}
